import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TimeSlotSelector } from './TimeSlotSelector';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { getBusinessActivities } from '@/utils/businessAppApi';
import { TimeSlot, ActivityTimeSlotFormData } from '@/types/calendarManagementTypes';
import { 
  Calendar, 
  Clock, 
  Users, 
  DollarSign, 
  Settings,
  Sparkles,
  CheckCircle,
  Activity,
  CalendarDays,
  Timer,
  MapPin
} from 'lucide-react';

interface AddActivityTimeSlotsProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ActivityTimeSlotFormData) => void;
}

export function AddActivityTimeSlots({ isOpen, onClose, onSubmit }: AddActivityTimeSlotsProps) {
  // State for form fields
  const [activityId, setActivityId] = useState('');
  const [dateRange, setDateRange] = useState<string[]>(['', '']);
  const [frequency, setFrequency] = useState<'Workdays' | 'Weekends' | 'All'>('Workdays');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [capacity, setCapacity] = useState({ total: 20, minRequired: 5 });
  const [price, setPrice] = useState(99.99);

  // Fetch activities for the select dropdown
  const { data: activities } = useQuery({
    queryKey: ['business-activities'],
    queryFn: getBusinessActivities
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTimeSlots([]); // Start with no time slots
      setActivityId('');
      setDateRange(['', '']);
      setFrequency('Workdays');
      setCapacity({ total: 20, minRequired: 5 });
      setPrice(99.99);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!activityId || dateRange.some(date => !date) || !timeSlots.length) {
      // Show error message
      return;
    }

    onSubmit({
      activityId,
      dateRange: dateRange.map(date => new Date(date).toISOString()),
      frequency,
      timeSlots,
      capacity,
      price
    });
  };

  // Get selected activity details
  const selectedActivity = activities?.find(activity => activity._id === activityId);

  // Calculate form progress
  const getFormProgress = () => {
    const steps = [
      !!activityId,
      dateRange.every(date => !!date),
      !!frequency,
      timeSlots.length > 0,
      capacity.total > 0 && capacity.minRequired > 0,
      price > 0
    ];
    return Math.round((steps.filter(Boolean).length / steps.length) * 100);
  };

  const formProgress = getFormProgress();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto border border-slate-200/60 shadow-xl shadow-slate-900/10">
        <DialogHeader className="pb-6 border-b border-slate-200/50">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-emerald-500 to-purple-500 flex items-center justify-center shadow-md">
                <Sparkles className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
                Create Activity Time Slots
              </DialogTitle>
              <p className="text-slate-600 mt-1 leading-relaxed">
                Schedule new time slots for your wildlife experiences
              </p>
            </div>
          </div>
          
          {/* Progress Indicator */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Setup Progress</span>
              <span className="font-medium text-slate-900">{formProgress}% Complete</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${formProgress}%` }}
              />
            </div>
          </div>

          {/* Status Pills */}
          <div className="flex items-center gap-3 pt-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-sm font-medium text-emerald-700">Setup Active</span>
            </div>
            {selectedActivity && (
              <Badge className="bg-blue-100 text-blue-800 border border-blue-200/50 shadow-sm">
                {selectedActivity.name}
              </Badge>
            )}
            {timeSlots.length > 0 && (
              <Badge className="bg-purple-100 text-purple-800 border border-purple-200/50 shadow-sm">
                {timeSlots.length} Time Slots
              </Badge>
            )}
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-8 pt-6">
          {/* Activity Selection Card */}
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center shadow-sm">
                  <Activity className="w-4 h-4 text-blue-600" />
                </div>
                Activity Selection
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <div className="space-y-2">
                <Label htmlFor="activity" className="text-sm font-medium text-slate-700">Choose Activity</Label>
                <Select onValueChange={setActivityId} value={activityId}>
                  <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80 shadow-sm">
                    <SelectValue placeholder="Select an activity to schedule" />
                  </SelectTrigger>
                  <SelectContent>
                    {activities?.map((activity) => (
                      <SelectItem key={activity._id} value={activity._id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-500" />
                          {activity.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-600">
                  Select the wildlife experience you want to create time slots for
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Date Range & Schedule Card */}
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-200/50 flex items-center justify-center shadow-sm">
                  <CalendarDays className="w-4 h-4 text-emerald-600" />
                </div>
                Schedule Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-medium text-slate-700">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange[0]}
                    onChange={(e) => setDateRange([e.target.value, dateRange[1]])}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-medium text-slate-700">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange[1]}
                    onChange={(e) => setDateRange([dateRange[0], e.target.value])}
                    min={dateRange[0]}
                    className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm"
                  />
                </div>
              </div>

              {/* Frequency */}
              <div className="space-y-2">
                <Label htmlFor="frequency" className="text-sm font-medium text-slate-700">Schedule Frequency</Label>
                <Select onValueChange={(value) => setFrequency(value as 'Workdays' | 'Weekends' | 'All')} value={frequency}>
                  <SelectTrigger className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Workdays">
                      <div className="flex items-center gap-2">
                        <Timer className="w-4 h-4 text-slate-500" />
                        Workdays (Mon-Fri)
                      </div>
                    </SelectItem>
                    <SelectItem value="Weekends">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        Weekends (Sat-Sun)
                      </div>
                    </SelectItem>
                    <SelectItem value="All">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-slate-500" />
                        All Days
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-600">
                  Choose which days of the week to create slots for
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Time Slots Card */}
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-lg bg-purple-100 border border-purple-200/50 flex items-center justify-center shadow-sm">
                  <Clock className="w-4 h-4 text-purple-600" />
                </div>
                Time Slots
                {timeSlots.length > 0 && (
                  <Badge className="bg-purple-100 text-purple-800 border border-purple-200/50 shadow-sm ml-2">
                    {timeSlots.length} slots
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <TimeSlotSelector
                timeSlots={timeSlots}
                onChange={setTimeSlots}
                defaultTimeSlot={{ startTime: '', endTime: '' }}
              />
              <p className="text-xs text-slate-600">
                Add multiple time slots for each day. Each slot will be created for all selected dates.
              </p>
            </CardContent>
          </Card>

          {/* Capacity & Pricing Card */}
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-lg bg-amber-100 border border-amber-200/50 flex items-center justify-center shadow-sm">
                  <Settings className="w-4 h-4 text-amber-600" />
                </div>
                Capacity & Pricing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              {/* Capacity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="totalCapacity" className="text-sm font-medium text-slate-700">Total Capacity</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="totalCapacity"
                      type="number"
                      value={capacity.total}
                      onChange={(e) => setCapacity({ ...capacity, total: parseInt(e.target.value) })}
                      min={1}
                      className="pl-10 focus:ring-amber-500 focus:border-amber-500 border-slate-200/80 shadow-sm"
                    />
                  </div>
                  <p className="text-xs text-slate-600">Maximum participants per slot</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="minRequired" className="text-sm font-medium text-slate-700">Minimum Required</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="minRequired"
                      type="number"
                      value={capacity.minRequired}
                      onChange={(e) => setCapacity({ ...capacity, minRequired: parseInt(e.target.value) })}
                      min={1}
                      max={capacity.total}
                      className="pl-10 focus:ring-amber-500 focus:border-amber-500 border-slate-200/80 shadow-sm"
                    />
                  </div>
                  <p className="text-xs text-slate-600">Minimum to confirm the experience</p>
                </div>
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="price" className="text-sm font-medium text-slate-700">Price per Person</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="price"
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(parseFloat(e.target.value))}
                    min={0}
                    step={0.01}
                    className="pl-10 focus:ring-amber-500 focus:border-amber-500 border-slate-200/80 shadow-sm"
                  />
                </div>
                <p className="text-xs text-slate-600">
                  Base price for adults (child and student discounts applied automatically)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-slate-200/60">
            <Button 
              type="button"
              variant="outline" 
              onClick={onClose}
              className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={formProgress < 100}
              className="bg-emerald-600 hover:bg-emerald-700 px-8 shadow-md hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Create {timeSlots.length > 0 ? timeSlots.length : ''} Time Slots
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}